import type { ReferralInviteOutcome, ReferralOffer } from 'hooks/api/useReferrals'

/**
 * VRG-655 — the COPY for "Refer & earn", in one place, built from the API's NUMBERS.
 *
 * The numbers come from `REFERRAL_OFFER` on the API (returned on every `me/link` and
 * `me/summary` call), never from literals here, so the modal, the page and the invite email
 * cannot quote one discount while the coupon applies another. Only the words live here.
 *
 * Brand rules this copy is held to (VRG-649 / vera-inc brand canon):
 *  - the programme is "Refer & earn" (Spencer, 2026-10-07, VRG-680; it was "Refer a brand"),
 *    sentence case with an ampersand: never "Refer and earn" or "Refer & Earn";
 *  - credits are ACTIVATIONS, never dollars, and they do not expire (VRG-649 decision 5), so
 *    nothing here mentions an expiry;
 *  - no commission vocabulary — the referrer receives activations, not a payout;
 *  - the referred brand's side matches vera.inc's own `/refer` page word for word, turned to
 *    the third person ("your" → "their"): "20% off your base plan for your first three
 *    months" / "5% off your first annual invoice". "Base plan", not "plan", because metered
 *    activations are not discounted.
 *  - no em-dash-joined clauses in UI copy (working-conventions §15).
 */

/** "three", not "3" — small counts read as words in copy, matching vera.inc. */
const WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve']
const asWord = (n: number) => WORDS[n] ?? String(n)

export const formatCount = (n: number) => n.toLocaleString('en-US')

export const referralOfferCopy = (offer: ReferralOffer) => {
  const r = offer.referrer
  const e = offer.referee
  return {
    /** What the referring company receives. */
    youTitle: 'For you',
    youBody: `Activations worth ${r.reward_percent}% of what they pay Vera in their first ${r.window_months} months.`,
    youDetail: `At least ${formatCount(r.floor_activations)}, up to ${formatCount(r.cap_activations)} per brand. Added to your company's balance each time they pay.`,
    /** What the referred brand receives. Mirrors vera.inc/refer. */
    themTitle: 'For them',
    themMonthly: `${e.monthly_discount_percent}% off their base plan for their first ${asWord(e.monthly_discount_months)} months.`,
    themAnnual: `Or ${e.annual_discount_percent}% off their first annual invoice.`,
  }
}

/**
 * The per-address result line after an invite.
 *
 * NEUTRAL BY DESIGN. The API returns `sent` for an address that is already a Vera customer,
 * already waiting, another brand's referral or an Insider's, and does not email it — so the
 * response never reveals who is a customer (VRG-654, AC3). This copy must not undo that:
 * "Sent" says the invitation was handled, nothing more. Only facts about the SENDER'S OWN
 * company are disclosed (`already_referred`, `ineligible_self_referral`).
 */
export const INVITE_OUTCOME_COPY: Record<ReferralInviteOutcome, { label: string; tone: 'ok' | 'muted' | 'error' }> = {
  sent: { label: 'Sent', tone: 'ok' },
  already_referred: { label: 'Already referred by your company', tone: 'muted' },
  ineligible_self_referral: { label: 'Not sent. This address is part of your own company', tone: 'muted' },
  not_sent_region_paused: { label: 'Not sent. Invitations to this region are paused for now', tone: 'muted' },
  failed: { label: 'Could not be sent. Try again later', tone: 'error' },
}

/** Max addresses per send, matching `REFERRAL_INVITES_PER_REQUEST` on the API. */
export const REFERRAL_INVITES_PER_REQUEST = 10

/** Matches the API DTO's `MaxLength(500)` on the note. */
export const REFERRAL_NOTE_MAX = 500
